
import { Publication } from '@bruce.work/bruce-doc-class-typescript/doc/Class/Publication';
import { Recorded } from '@bruce.work/bruce-doc-event-typescript/doc/Class';
import { BACKGROUND_COLOR } from 'interfaces';
import dynamic from 'next/dynamic';
import { summarySectionInsertModel } from 'pages/genericPage/config';
import { INJECTABLE_SECTIONS, Page } from './type';
import Summary from '@/components/blocks/Summary';
import Spinner from '@/components/molecules/Spinner';
import SectionCta from '@/components/sections/SectionCta';
import SectionIntro from '@/components/sections/SectionIntro';
import SectionSplitWithImage from '@/components/sections/SectionSplitWithImage';
import SectionWideWithImage from '@/components/sections/SectionWideWithImage';
import SectionWithCtaCard from '@/components/sections/SectionWithCtaCard';
import SectionWithCtaIconCard from '@/components/sections/SectionWithCtaIconCard';
import SectionWithExternalContentCard from '@/components/sections/SectionWithExternalContentCard';
import SectionWithIconCard from '@/components/sections/SectionWithIconCard';
import SectionWithIconParagraph from '@/components/sections/SectionWithIconParagraph';
import SectionWithImageAndCtaCard from '@/components/sections/SectionWithImageAndCtaCard';
import SectionWithJobPlaceCard from '@/components/sections/SectionWithJobPlaceCard';
import SectionWithRichText from '@/components/sections/SectionWithRichText';
import SectionWithSector from '@/components/sections/SectionWithSector';
import SectionWithUserCard from '@/components/sections/SectionWithUserCard';
import SectionWithVideoStepper from '@/components/sections/SectionWithVideoStepper';
import { SpacingWrapper } from '@/components/styled';
import { transformTitleArrayToString, transformTitleToAnchorId } from '@/utilities/functions';
const SectionCarousel = dynamic(() => import('@/components/sections/SectionCarousel'), { loading: () => <Spinner /> });
const SectionDemo = dynamic(() => import('@/components/sections/SectionDemo'), { loading: () => <Spinner /> });
const SectionInfiniteCarousel = dynamic(() => import('@/components/sections/SectionInfiniteCarousel'), { loading: () => <Spinner /> });
const SectionWithHubspotPosts = dynamic(() => import('@/components/sections/SectionWithHubspotPosts'), { loading: () => <Spinner /> });
const SectionWithImageStepper = dynamic(() => import('@/components/sections/SectionWithImageStepper'), { loading: () => <Spinner /> });
const SectionWithMissionCard = dynamic(() => import('@/components/sections/SectionWithMissionCard'), { loading: () => <Spinner /> });

// #region INSERT SECTION AT SPECIFIC INDEX IN ARRAY
// helper to inject in a specified array, one or more section at a specific index.
export const insertSection = ({ summaryEnabled, targetArray, sectionsToInsert }: {
	summaryEnabled: boolean,
	targetArray: any[];
	sectionsToInsert: { [index: number]: { [index: string]: any } };
}) => {

	const customSectionsToInsert: { [index: number]: { [index: string]: any } } =
		summaryEnabled ? { ...sectionsToInsert, ...summarySectionInsertModel } : sectionsToInsert;

	return targetArray.reduce(
		(sections, currentSection, index) => {
			customSectionsToInsert[index] ? sections.push(customSectionsToInsert[index], currentSection) : sections.push(currentSection);
			return sections;
		},
		[],
	);
};
// #endregion

// #region RENDER-COMPONENTS
export const renderComponents = ({ body, component, customReactElementPerPage, index, publications, currentLang }: {
	/**
	 * The entire page body array.
	 */
	body?: Page['body'];
	/**
	 * Component to render, defined from Strapi CMS.
	 */
	component: Page['body']['0'];
	/**
	 * Defines custom React element to pass into children of customizable components.
	 */
	customReactElementPerPage?: any;
	/**
	 * Index of the component.
	 */
	index: number;
	/**
	 * Publications data for SectionWithMissionCard components.
	 */
	publications?: Recorded<Publication>[];
	/**
	 * Current language/locale
	 */
	currentLang?: string;
}) => {

	switch (component.__typename) {
		case 'ComponentSectionCarousel': {
			return (
				<SpacingWrapper
					backgroundColor={component.backgroundColor.color}
					key={index}
					id={transformTitleToAnchorId(transformTitleArrayToString(component.title.titlePieces))}
				>
					<div>
						<SectionCarousel carouselData={component} />
					</div>
				</SpacingWrapper>
			);
		}
		case 'ComponentSectionInfiniteCarousel': {
			return (
				<SpacingWrapper
					backgroundColor={component.backgroundColor.color}
					key={index}
					id={transformTitleToAnchorId(transformTitleArrayToString(component.title.titlePieces))}
				>
					<div>
						<SectionInfiniteCarousel infiniteCarouselData={component} />
					</div>
				</SpacingWrapper>
			);
		}
		case 'ComponentSectionIntro': {
			return (
				<SectionIntro key={index} sectionIntroData={component}>
					{customReactElementPerPage?.[component.__typename]}
				</SectionIntro>
			);
		}
		case 'ComponentSectionWideWithImage': {
			return (
				<SpacingWrapper
					backgroundColor={component.backgroundColor.color}
					key={index}
					id={transformTitleToAnchorId(transformTitleArrayToString(component.title.titlePieces))}
				>
					<div>
						<SectionWideWithImage />
					</div>
				</SpacingWrapper>
			);
		}
		case 'ComponentSectionWithCtaCard': {
			return (
				<SpacingWrapper
					backgroundColor={component.backgroundColor.color}
					key={index}
					id={transformTitleToAnchorId(transformTitleArrayToString(component.title.titlePieces))}
				>
					<div>
						<SectionWithCtaCard sectionWithCtaCardData={component} />
					</div>
				</SpacingWrapper>
			);
		}
		case 'ComponentSectionWithCtaIconCard': {
			return (
				<SpacingWrapper
					backgroundColor={component.backgroundColor.color}
					key={index}
					id={transformTitleToAnchorId(transformTitleArrayToString(component.title.titlePieces))}
				>
					<div>
						<SectionWithCtaIconCard sectionWithCtaIconCardData={component} />
					</div>
				</SpacingWrapper>
			);
		}
		case 'ComponentSectionWithIconCard': {
			return (
				<SpacingWrapper
					backgroundColor={component.backgroundColor.color}
					key={index}
					id={transformTitleToAnchorId(transformTitleArrayToString(component.title.titlePieces))}
				>
					<div>
						<SectionWithIconCard sectionWithIconCardData={component} />
					</div>
				</SpacingWrapper>
			);
		}
		case 'ComponentSectionWithSector': {
			return (
				<SpacingWrapper
					backgroundColor={component.backgroundColor.color}
					key={index}
					id={transformTitleToAnchorId(transformTitleArrayToString(component.title.titlePieces))}
				>
					<div>
						<SectionWithSector sectionWithSectorData={component} />
					</div>
				</SpacingWrapper>
			);
		}
		case 'ComponentSectionWithVideoStepper': {
			return (
				<SpacingWrapper
					backgroundColor={component.backgroundColor.color}
					key={index}
					id={transformTitleToAnchorId(transformTitleArrayToString(component.title.titlePieces))}
				>
					<div>
						<SectionWithVideoStepper sectionWithVideoStepperData={component} />
					</div>
				</SpacingWrapper>
			);
		}
		case 'ComponentSectionSplitWithImage': {
			return (
				<span
					key={index}
					id={transformTitleToAnchorId(transformTitleArrayToString(component.title.titlePieces))}
				>
					<SectionSplitWithImage key={index} sectionSplitWithImageData={component} />
				</span>
			);
		}
		case 'ComponentSectionWithIconParapraph': {
			return (
				<SpacingWrapper
					backgroundColor={component.backgroundColor.color}
					key={index}
					id={component.title ? transformTitleToAnchorId(transformTitleArrayToString(component.title.titlePieces)) : ''}
				>
					<div>
						<SectionWithIconParagraph sectionIconParagraphData={component} />
					</div>
				</SpacingWrapper>
			);
		}
		case 'ComponentSectionWithImageStepper': {
			return (
				<SpacingWrapper
					backgroundColor={component.backgroundColor.color}
					key={index}
					id={transformTitleToAnchorId(transformTitleArrayToString(component.title.titlePieces))}
				>
					<div>
						<SectionWithImageStepper sectionWithImageStepperData={component} />
					</div>
				</SpacingWrapper>
			);
		}
		case 'ComponentSectionWithImgAndCard': {
			return (
				<SpacingWrapper
					backgroundColor={component.backgroundColor.color}
					key={index}
					id={transformTitleToAnchorId(transformTitleArrayToString(component.title.titlePieces))}
				>
					<div>
						<SectionWithImageAndCtaCard sectionWithImageAndCtaCardData={component} />
					</div>
				</SpacingWrapper>
			);
		}
		case 'ComponentSectionWithJobPlaceCard': {
			return (
				<SpacingWrapper
					backgroundColor={component.backgroundColor.color}
					key={index}
					id={transformTitleToAnchorId(transformTitleArrayToString(component.title.titlePieces))}
				>
					<div>
						<SectionWithJobPlaceCard sectionWithJobPlaceCardData={component} />
					</div>
				</SpacingWrapper>
			);
		}
		case 'ComponentSectionWithExternalContentCard': {
			return (
				<SpacingWrapper
					backgroundColor={component.backgroundColor.color}
					key={index}
					id={transformTitleToAnchorId(transformTitleArrayToString(component.title.titlePieces))}
				>
					<div>
						<SectionWithExternalContentCard sectionWithExternalContentCardData={component} />
					</div>
				</SpacingWrapper>
			);
		}
		case 'ComponentSectionDemo': {
			return (
				<SpacingWrapper
					backgroundColor={component.backgroundColor.color}
					key={index}
					id={transformTitleToAnchorId(transformTitleArrayToString(component.title.titlePieces))}
				>
					<div>
						<SectionDemo sectionDemoData={component} />
					</div>
				</SpacingWrapper>
			);
		}
		case 'ComponentSectionWithUserCard': {
			return (
				<SpacingWrapper
					backgroundColor={component.backgroundColor.color}
					key={index}
					id={transformTitleToAnchorId(transformTitleArrayToString(component.title.titlePieces))}
				>
					<div>
						<SectionWithUserCard sectionWithUserCardData={component} />
					</div>
				</SpacingWrapper>
			);
		}
		case 'ComponentSectionCta': {
			return (
				<SpacingWrapper
					backgroundColor={component.backgroundColor.color}
					key={index}
					id={transformTitleToAnchorId(transformTitleArrayToString(component.title.titlePieces))}
				>
					<div>
						<SectionCta SectionCtaData={component} />
					</div>
				</SpacingWrapper>
			);
		}
		case 'ComponentParagraphRichText': {
			return (
				<SpacingWrapper
					backgroundColor={BACKGROUND_COLOR.white}
					key={index}
					padding='6rem 2rem 0rem 2rem'
				>
					<div>
						<SectionWithRichText {...component} />
					</div>
				</SpacingWrapper>
			);
		}
		case INJECTABLE_SECTIONS.SUMMARY: {
			return (
				<SpacingWrapper
					backgroundColor={BACKGROUND_COLOR.white}
					key={index}
					padding={body?.[1].__typename === 'ComponentParagraphRichText'
						? '0rem 2rem 6rem 2rem'
						: undefined
					}
				>
					<div>
						<Summary
							summary={
								body?.filter((section) => section.__typename !== 'ComponentSectionIntro')
									.reduce(
										(titles: string[], currentSection) => {
											currentSection.title && titles.push(transformTitleArrayToString(currentSection.title.titlePieces));
											return titles;
										},
										[],
									)
							}
						/>
					</div>
				</SpacingWrapper>
			);
		}
		case INJECTABLE_SECTIONS.MISSION_CARDS_PREVIEW: {
			return (
				<SpacingWrapper
					backgroundColor={component.backgroundColor.color}
					id={transformTitleToAnchorId(transformTitleArrayToString(component.title.titlePieces))}
					key={index}
				>
					<div>
						<SectionWithMissionCard sectionWithMissionCardData={{...component, publications}} currentLang={currentLang} />
					</div>
				</SpacingWrapper>
			);
		}
		case INJECTABLE_SECTIONS.HUBSPOT_BLOG_POSTS: {
			return (
				<SpacingWrapper
					backgroundColor={BACKGROUND_COLOR.white}
					id={transformTitleToAnchorId(transformTitleArrayToString(component.title.titlePieces))}
					key={index}
				>
					<div>
						<SectionWithHubspotPosts sectionWithHubspotPostData={component} />
					</div>
				</SpacingWrapper>
			);
		}
		default: {
			return (
				<SpacingWrapper backgroundColor={BACKGROUND_COLOR.white} key={index}>
					<div>
						. . .
					</div>
				</SpacingWrapper>
			);
		}
	}
};
// #endregion
